import { useCallback, useEffect, useState } from 'react'

import { useDispatch } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import statusActions from 'actions/status'
import { PaymentProps } from 'routes/Checkout/Steps/Payment/Payment'
import { fireCheckoutError, setPayPalNonce } from 'routes/Checkout/checkoutActions'
import { CARD_DETAILS } from 'routes/Checkout/checkoutConfig'

import { formatAddress } from '../utils/utils'

const initialState = {
  errorStatus: {
    showError: false,
    isEmpty: true,
  },
  isFormValid: false,
  isFormSubmitting: false,
}

const hostedFieldsOptions = {
  number: {
    container: `#${CARD_DETAILS.CARD_NUMBER}`,
    formatInput: true,
  },
  expirationDate: {
    container: `#${CARD_DETAILS.EXPIRY_DATE}`,
  },
  cvv: {
    container: `#${CARD_DETAILS.CVV}`,
  },
}

const hostedFieldStyles = {
  input: {
    color: '#516272',
    'font-family': 'AxiformaBook, Helvetica, sans-serif',
    'font-size': '16px',
    'line-height': '20px',
  },
}
type useHostedFieldsProps = Pick<
  PaymentProps,
  'braintreeClientInstance' | 'submitOrder' | 'setBraintreeClientInstance'
>

export const useHostedFields = ({
  braintreeClientInstance,
  setBraintreeClientInstance,
  submitOrder,
}: useHostedFieldsProps) => {
  const [hostedFieldsInstance, setHostedFieldsInstance] = useState<braintree.HostedFields | null>(
    null,
  )
  const [threeDSecureInstance, setThreeDSecureInstance] = useState<braintree.ThreeDSecure | null>(
    null,
  )
  const [inputCardNumberError, setInputCardNumberError] = useState(initialState.errorStatus)
  const [inputExpiryDateError, setInputExpiryDateError] = useState(initialState.errorStatus)
  const [inputCvvError, setInputCvvError] = useState(initialState.errorStatus)
  const [isFormValid, setIsFormValid] = useState<boolean>(initialState.isFormValid)
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(initialState.isFormSubmitting)
  const dispatch = useDispatch()

  const handleLookUpComplete = useCallback(
    (data, next) => {
      if (data?.requiresUserAuthentication) {
        next()
      } else {
        dispatch(fireCheckoutError(actionTypes.VALID_CARD_DETAILS_NOT_PROVIDED))
        setIsFormSubmitting(false)
      }
    },
    [dispatch],
  )

  const handleCustomerCancelledThreeDSecure = useCallback(
    () => dispatch(fireCheckoutError(actionTypes.VALID_CARD_DETAILS_NOT_PROVIDED)),
    [dispatch],
  )

  // when user submits the form
  const handleSubmit = async (options: Record<string, any>) => {
    if (!hostedFieldsInstance || !threeDSecureInstance) return
    setIsFormSubmitting(true)

    try {
      setIsFormSubmitting(true)
      const payload = await hostedFieldsInstance.tokenize()

      threeDSecureInstance.on('lookup-complete', handleLookUpComplete)
      threeDSecureInstance.on('customer-canceled', handleCustomerCancelledThreeDSecure)

      const { deliveryAddress, values } = options
      const address = formatAddress(deliveryAddress)
      const formValues = values.isBillingAddressDifferent ? values : address

      const result = await threeDSecureInstance.verifyCard({
        nonce: payload.nonce,
        amount: '0.0' as any,
        challengeRequested: true,
        bin: payload.details.bin,
        additionalInformation: {
          acsWindowSize: '03',
        },
        billingAddress: {
          streetAddress: formValues.houseNo,
          postalCode: formValues.postcode,
          locality: formValues.town,
        },
      })

      if (result.threeDSecureInfo.liabilityShifted) {
        dispatch(setPayPalNonce(result.nonce))
        submitOrder()
      } else {
        dispatch(fireCheckoutError(actionTypes.VALID_CARD_DETAILS_NOT_PROVIDED))
      }
    } catch (error) {
      dispatch(fireCheckoutError(actionTypes.VALID_CARD_DETAILS_NOT_PROVIDED))
    } finally {
      setIsFormSubmitting(false)
    }
  }

  // when user clicks submit without filling form details
  const handleCardDetailsValidation = () => {
    if (inputCardNumberError.isEmpty) {
      setInputCardNumberError({ ...inputCardNumberError, showError: true })
    }
    if (inputCvvError.isEmpty) {
      setInputCvvError({ ...inputCvvError, showError: true })
    }
    if (inputExpiryDateError.isEmpty) {
      setInputExpiryDateError({
        ...inputExpiryDateError,
        showError: true,
      })
    }
  }

  const handleOnValidityChanged = useCallback((event: any) => {
    const errorField = event.emittedBy
    const formValid = Object.keys(event.fields).every((key) => event.fields[key].isValid)
    setIsFormValid(formValid)

    const { isEmpty, isValid } = event.fields[errorField]
    switch (event.emittedBy) {
      case 'number':
        return setInputCardNumberError({
          showError: !isValid,
          isEmpty,
        })
      case 'expirationDate':
        return setInputExpiryDateError({
          showError: !isValid,
          isEmpty,
        })
      case 'cvv':
        return setInputCvvError({ showError: !isValid, isEmpty })
      default:
        return undefined
    }
  }, [])

  const initThreeDSecure = useCallback(async () => {
    const createThreeDSecureInstance = async () => {
      if (!braintreeClientInstance) return
      const threeDSecure = await braintree.threeDSecure.create({
        client: braintreeClientInstance,
        version: 2,
      })
      setThreeDSecureInstance(threeDSecure)
    }
    try {
      await createThreeDSecureInstance()
    } catch (error) {
      dispatch(statusActions.error(actionTypes.HOSTED_FIELDS_CREATION_ERROR, true))
    }
  }, [braintreeClientInstance, dispatch])

  const initHostedFields = useCallback(async () => {
    const createHostedFieldInstance = async () => {
      if (!braintreeClientInstance) return
      const hostedField = await braintree.hostedFields.create({
        client: braintreeClientInstance,
        fields: hostedFieldsOptions,
        styles: hostedFieldStyles,
      })
      setHostedFieldsInstance(hostedField)
    }
    try {
      await createHostedFieldInstance()
    } catch (error) {
      dispatch(statusActions.error(actionTypes.HOSTED_FIELDS_CREATION_ERROR, true))
    }
  }, [braintreeClientInstance, dispatch])

  const initCardDetailsValidation = useCallback(async () => {
    if (!hostedFieldsInstance) return

    hostedFieldsInstance.on('validityChange', handleOnValidityChanged)
    hostedFieldsInstance.on('blur', handleOnValidityChanged)
  }, [handleOnValidityChanged, hostedFieldsInstance])

  // to initalise hostedfields client
  useEffect(() => {
    if (braintreeClientInstance) {
      initHostedFields()
    }
  }, [braintreeClientInstance, initHostedFields])

  // to initalise 3DSecure
  useEffect(() => {
    if (braintreeClientInstance) {
      initThreeDSecure()
    }
  }, [braintreeClientInstance, initThreeDSecure])

  // do card verification
  useEffect(() => {
    if (hostedFieldsInstance) {
      initCardDetailsValidation()
    }
  }, [hostedFieldsInstance, initCardDetailsValidation])

  useEffect(
    () => () => {
      if (braintreeClientInstance) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore the type here is incorrect, callback is optional
        braintreeClientInstance.teardown()
        setBraintreeClientInstance(null)
      }
    },

    [braintreeClientInstance, setBraintreeClientInstance],
  )

  return {
    formValidationError: {
      showCardNumberError: inputCardNumberError.showError,
      showCVVError: inputCvvError.showError,
      showExpiryDateError: inputExpiryDateError.showError,
    },
    isFormValid,
    handleCardDetailsValidation,
    handleOnValidityChanged,
    setHostedFieldsInstance,
    setThreeDSecureInstance,
    handleSubmit,
    isFormSubmitting,
    handleLookUpComplete,
    handleCustomerCancelledThreeDSecure,
  }
}
